import {useCallback, useEffect, useRef} from "react";
import {useParams} from "react-router-dom";
import {useSelector} from "react-redux";

import type {RootState} from "../../../store";

import {getOrderByCreatedDate} from "../../../utils/DateFormatting";
import useConstructionNewHook from "../../../hooks/useConstructionNewHook";
import useCalculationNewHook from "../../../hooks/useCalculationNewHook";
import type ConstructionListItem from "../../constructionsNew/types/ConstructionListItem";
import type ConstructionAllDetailsGuiDto from "../../../dto/construction/ConstructionAllDetailsGuiDto";
import type CalculationListItemDto from "../../../dto/calculations/CalculationListItemDto";
import type CalculationResultDto from "../../../dto/calculations/CalculationResultDto";
import type { CalculationConstructionDetailsResponseDto } from "../../../dto/calculations/CalculationConstructionDetailsDto";
import type CalculationNoteMatGroundType from "../../../dto/calculations/CalculationNoteMatGroundTypeDto";
import type ApiResponse from "../../../services/api/types/ApiResponse";

interface UseConstructionCalculationsFetchingHookResult {
  getConstructions: () => Promise<ConstructionListItem[]>;
  getCurrentConstruction: () => Promise<ConstructionAllDetailsGuiDto | null>;
  getCalculations: () => Promise<CalculationListItemDto[]>;
  getCalculationProfiles: (
    selectedCalculation: CalculationListItemDto | undefined
  ) => Promise<CalculationResultDto | null>;
  getNextPreviousCalculationIds: (
    calculationId: string | undefined | null
  ) => Promise<string | undefined>;
  getConstructionDetails: (
    calculationId: string
  ) => Promise<ApiResponse<CalculationConstructionDetailsResponseDto>>;
  getCalculationNoteMatGroundTypesById: (
    calculationId: string
  ) => Promise<ApiResponse<CalculationNoteMatGroundType[]>>;
};


/** useConstructionCalculationsFetchingHook
 * fetches all the necessary data from the api's through their hooks
 */
const useConstructionCalculationsFetchingHook = (): UseConstructionCalculationsFetchingHookResult => {
  const {id} = useParams();
  const {all} = useSelector((state: RootState) => state.constructionsNew);
  const allRef = useRef(all);
  useEffect(() => {
    allRef.current = all;
  }, [all]);
  const {getAllConstructions, getConstructionById} = useConstructionNewHook();
  const {
    getCalculationsByConstructionId,
    getCalculationResults,
    getConstructionDetails,
    getCalculationNoteMatGroundTypesById,
    getLatestCalculationByConstructionId,
  } = useCalculationNewHook();

  const getConstructions = useCallback(async (): Promise<ConstructionListItem[]> => {
    if (allRef.current.length <= 0) {
      const response = await getAllConstructions();
      return getOrderByCreatedDate(response?.data ?? []);
    }
    return allRef.current;
  }, [getAllConstructions]);

  const getCurrentConstruction = useCallback(async (): Promise<ConstructionAllDetailsGuiDto | null> => {
    if (id) {
      const response = await getConstructionById(id);
      return response.data;
    }
    return null;
  }, [getConstructionById, id]);

  const getCalculations = useCallback(async (): Promise<CalculationListItemDto[]> => {
    if (id !== undefined) {
      const response = await getCalculationsByConstructionId(id);
      return (
        response.data?.sort(
          (a: CalculationListItemDto, b: CalculationListItemDto) =>
            new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
        ) ?? []
      );
    }
    return [];
  }, [getCalculationsByConstructionId, id]);

  const getCalculationProfiles = useCallback(
    async (selectedCalculation: CalculationListItemDto | undefined): Promise<CalculationResultDto | null> => {
      if (selectedCalculation !== undefined) {
        const response = await getCalculationResults(selectedCalculation.calculationId);
        return response.data;
      }
      return null;
    },
    [getCalculationResults],
  );

  const getNextPreviousCalculationIds = useCallback(
    async (calculationId: string | undefined | null): Promise<string | undefined> => {
      if (calculationId) {
        const result = await getLatestCalculationByConstructionId(calculationId);
        return result.data?.calculationId;
      }
    },
    [getLatestCalculationByConstructionId],
  );

  return {
    getConstructions,
    getCurrentConstruction,
    getCalculations,
    getCalculationProfiles,
    getNextPreviousCalculationIds,
    getConstructionDetails,
    getCalculationNoteMatGroundTypesById,
  };
};

export default useConstructionCalculationsFetchingHook;
